import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Stack from '@mui/material/Stack';

import documentationSelector from './documentationSelector';
import documentationHelpers from './helpers/documentationHelpers';
import { addDocumentation, editDocumentation, deleteDocumentation, loadDocumentation } from './documentationReducer';
import sort from '../common/helpers/sort';
import transformer from '../common/helpers/transformer';
import AddItemsFormField from '../common/components/AddItemsFormField';
import type { Documentation, DocumentationType } from '../common/types/documentation';
import type { AppDispatch } from '../store';
import filter from '../common/helpers/filter';
import ShortContacts from './ShortContacts';
import { loadGroups } from '../settings/settingsReducer';
import { getDocumentationPayloadType } from '../common/helpers/payloadType';
import { useLocation } from 'wouter';
import { getGroups } from './helpers/groups';
import getFormConfig from './helpers/formConfig';

type Props = {
  year: string;
};

function DocumentationForm({ year }: Props) {
  const [location] = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const documentations = useSelector(documentationSelector.getDocumentation);
  const groups = getGroups(useSelector);
  const filteredDocs = documentations.filter((doc) => filter.isInYear(doc.date, year));
  const shortContactDocs = filteredDocs.find((doc) => doc.type === 'shortContact');

  const formConfig = getFormConfig(location, groups);

  const payloadType = getDocumentationPayloadType(location);

  const updateItem = async (item: Documentation, isNewItem: boolean, currentType: DocumentationType) => {
    const { id, type, ...documentation } = item;
    const payload = await transformer.fromObjectToBase64WithEncryption({ type: currentType, ...documentation });

    if (isNewItem) {
      await dispatch(addDocumentation({ id, payloadType, payload }));
    } else {
      await dispatch(editDocumentation({ id, payload }));
    }
    dispatch(loadDocumentation(payloadType));
  };

  const deleteItem = (id: string) => {
    dispatch(deleteDocumentation(id));
    dispatch(loadDocumentation(payloadType));
  };

  React.useEffect(() => {
    dispatch(loadDocumentation(payloadType));
    dispatch(loadGroups());
  }, []);

  return (
    <Stack direction='column' px={1} maxWidth='650px'>
      <ShortContacts year={year} shortContactDocs={shortContactDocs} updateItem={updateItem} />
      {formConfig.map((formItem) => (
        <AddItemsFormField
          key={formItem.key}
          expanded
          items={documentationHelpers
            .filterDocumentationsByType(filteredDocs, formItem.key)
            .sort((itemA, itemB) => sort.sortByDate(itemA.date, itemB.date, 'DESC'))}
          form={formItem.form}
          renderItemEntry={(item) => formItem.renderItems(item, formItem.options)}
          updateItem={(updatedItem, isNewItem) => {
            return updateItem(updatedItem, isNewItem, formItem.key);
          }}
          deleteItem={(updatedItem) => deleteItem(updatedItem.id)}
        />
      ))}
    </Stack>
  );
}

export default DocumentationForm;
